import { useState, useRef, useMemo } from 'react';
import { addDoc, collection, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './CreateApp.css';
import MonetizeModal from '../components/MonetizeModal';

function CreateApp() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // New state for view mode and code files
  const [viewMode, setViewMode] = useState('render'); // 'render' or 'code'
  const [codeSegments, setCodeSegments] = useState({ html: '', css: '', js: '' });
  const [selectedFile, setSelectedFile] = useState('html');
  const [isSaveModalOpen, setSaveModalOpen] = useState(false);
  const [projectDetails, setProjectDetails] = useState({
    title: '',
    description: ''
  });
  const [isMonetizeModalOpen, setMonetizeModalOpen] = useState(false);
  const { user } = useAuth();
  const previewRef = useRef(null);
  const navigate = useNavigate();

  // Existing function for saving projects (if needed)
  const saveToFirebase = async () => {
    if (!user || !codeSegments) return;
    
    try {
      const projectData = {
        userId: user.uid,
        title: projectDetails.title || 'Untitled Project',
        description: projectDetails.description || '',
        messages: messages,
        code: {
          html: codeSegments.html || '',
          css: codeSegments.css || '',
          js: codeSegments.js || ''
        },
        createdAt: Date.now(),
        lastModified: Date.now()
      };

      const docRef = await addDoc(collection(db, 'projects'), projectData);
      setSaveModalOpen(false);
      // Navigate to the project view after saving
      navigate(`/project/${docRef.id}`);
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  // Handle opening save modal with initial project title
  const handleOpenSaveModal = () => {
    // Try to extract a title from the first user message or use default
    const initialTitle = messages[0]?.text?.split('\n')[0]?.slice(0, 50) || 'Untitled Project';
    setProjectDetails(prev => ({ ...prev, title: initialTitle }));
    setSaveModalOpen(true);
  };

  // New function to save each file as its own document in Firebase
  const saveFilesToFirebase = async (files) => {
    if (!user) return;
    try {
      const fileTypes = ['html', 'css', 'js'];
      const promises = fileTypes.map((type) => {
        if (files[type]) {
          return addDoc(collection(db, 'projectFiles'), {
            userId: user.uid,
            fileType: type,
            code: files[type],
            createdAt: Date.now(),
          });
        }
        return null;
      });
      await Promise.all(promises);
    } catch (error) {
      console.error('Error saving files:', error);
    }
  };

  const extractCodeFromResponse = (text) => {
    const htmlRegex = /\[\[HTML\]\]([\s\S]*?)\[\[\/HTML\]\]/;
    const cssRegex = /\[\[CSS\]\]([\s\S]*?)\[\[\/CSS\]\]/;
    const jsRegex = /\[\[JavaScript\]\]([\s\S]*?)\[\[\/JavaScript\]\]/;
    const htmlMatch = text.match(htmlRegex);
    const cssMatch = text.match(cssRegex);
    const jsMatch = text.match(jsRegex);
    return {
      html: htmlMatch ? htmlMatch[1].trim() : null,
      css: cssMatch ? cssMatch[1].trim() : null,
      js: jsMatch ? jsMatch[1].trim() : null,
    };
  };

  // Compute a blob URL for the rendered view using useMemo
  const renderSrc = useMemo(() => {
    if (codeSegments && (codeSegments.html || codeSegments.css || codeSegments.js)) {
      let cssURL = '';
      if (codeSegments.css) {
        const cssBlob = new Blob([codeSegments.css], { type: 'text/css' });
        cssURL = URL.createObjectURL(cssBlob);
      }
      const jsContent = codeSegments.js ? `<script>${codeSegments.js}</script>` : '';
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Preview</title>
            ${cssURL ? `<link rel="stylesheet" href="${cssURL}">` : ''}
          </head>
          <body>
            ${codeSegments.html || ''}
            ${jsContent}
          </body>
        </html>
      `;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      return URL.createObjectURL(htmlBlob);
    }
    return null;
  }, [codeSegments]);

  // Helper to create download URLs for each file type
  const getDownloadUrl = (fileType) => {
    if (!codeSegments) return null;
    let blob, filename;
    if (fileType === 'html' && codeSegments.html) {
      blob = new Blob([codeSegments.html], { type: 'text/html' });
      filename = 'generated.html';
    } else if (fileType === 'css' && codeSegments.css) {
      blob = new Blob([codeSegments.css], { type: 'text/css' });
      filename = 'generated.css';
    } else if (fileType === 'js' && codeSegments.js) {
      blob = new Blob([codeSegments.js], { type: 'application/javascript' });
      filename = 'generated.js';
    }
    if (blob) {
      return { url: URL.createObjectURL(blob), filename };
    }
    return null;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
  
    const userMessage = { text: newMessage, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);
  
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: "o3-mini",
          messages: [
            {
              role: "system",
              content: "You are a helpful AI that generates HTML/CSS code. Always respond with a brief explanation followed by the code. The HTML code should be enclosed between [[HTML]] and [[/HTML]] tags, and the CSS code should be enclosed between [[CSS]] and [[/CSS]] tags. The code should be valid HTML/CSS that can be directly rendered in a browser."
            },
            {
              role: "user",
              content: newMessage
            }
          ]
                })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${JSON.stringify(errorData)}`);
      }
  
      const data = await response.json();
      const aiResponse = data.choices[0].message.content;
      
      // Add AI message to chat
      const newMessages = [...messages, userMessage, { text: aiResponse, sender: 'bot' }];
      setMessages(newMessages);
  
      const codeSegments = extractCodeFromResponse(aiResponse);
      if (codeSegments.html || codeSegments.css) {
        setCodeSegments(codeSegments);
      }
      
    } catch (error) {
      console.error('Full error:', error);
      setMessages(prev => [...prev, { 
        text: `Error: ${error.message}`, 
        sender: 'bot' 
      }]);
    }
  
    setIsLoading(false);
  };

  const renderPreview = (preview) => {
    if (!preview?.html) return null;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>${preview.css || ''}</style>
        </head>
        <body>
          ${preview.html}
          ${preview.js ? `<script>${preview.js}</script>` : ''}
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    return URL.createObjectURL(blob);
  };

  return (
    <div className="create-app">
      <div className="chat-section">
        <div className="messages">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.sender}`}>
              <pre>{message.text}</pre>
            </div>
          ))}
          {isLoading && (
            <div className="message bot">
              <div className="loading">Thinking...</div>
            </div>
          )}
        </div>
        <form onSubmit={handleSendMessage} className="message-input">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Describe what you want to create..."
          />
          <button type="submit" disabled={isLoading}>
            Send
          </button>
        </form>
      </div>

      <div className="preview-section">
        {/* Save and Publish Buttons */}
        {user && codeSegments && (
          <div className="action-buttons">
            <button className="save-project-button" onClick={handleOpenSaveModal}>
              Save Project
            </button>
            <button className="publish-button" onClick={() => setMonetizeModalOpen(true)}>
              Publish to Store
            </button>
          </div>
        )}

        {/* Toggle buttons for Render/Code view */}
        <div className="view-toggle-buttons">
          <button onClick={() => setViewMode('render')}>Render</button>
          <button onClick={() => setViewMode('code')}>Code</button>
        </div>

        {viewMode === 'render' ? (
          renderSrc ? (
            <iframe
              src={renderSrc}
              style={{ width: '100%', height: '500px', border: 'none' }}
              title="Rendered Preview"
            />
          ) : (
            <div>No renderable code available.</div>
          )
        ) : (
          // Code view
          codeSegments ? (
            <div className="code-view">
              <div className="code-tabs">
                {codeSegments.html && (
                  <button
                    className={selectedFile === 'html' ? 'active' : ''}
                    onClick={() => setSelectedFile('html')}
                  >
                    HTML
                  </button>
                )}
                {codeSegments.css && (
                  <button
                    className={selectedFile === 'css' ? 'active' : ''}
                    onClick={() => setSelectedFile('css')}
                  >
                    CSS
                  </button>
                )}
                {codeSegments.js && (
                  <button
                    className={selectedFile === 'js' ? 'active' : ''}
                    onClick={() => setSelectedFile('js')}
                  >
                    JavaScript
                  </button>
                )}
              </div>
              <div className="code-content">
                <pre>
                  {selectedFile === 'html'
                    ? codeSegments.html
                    : selectedFile === 'css'
                    ? codeSegments.css
                    : codeSegments.js}
                </pre>
                <div className="download-link">
                  {(() => {
                    const download = getDownloadUrl(selectedFile);
                    return download ? (
                      <a href={download.url} download={download.filename}>
                        Download {selectedFile.toUpperCase()}
                      </a>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div>No code available.</div>
          )
        )}

        {/* Save Project Modal */}
        {isSaveModalOpen && (
          <div className="modal-overlay">
            <div className="save-modal">
              <button className="close-button" onClick={() => setSaveModalOpen(false)}>×</button>
              <h2>Save Project</h2>
              <form onSubmit={(e) => { e.preventDefault(); saveToFirebase(); }}>
                <div className="form-group">
                  <label>Project Title</label>
                  <input
                    type="text"
                    value={projectDetails.title}
                    onChange={(e) => setProjectDetails(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter project title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={projectDetails.description}
                    onChange={(e) => setProjectDetails(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter project description"
                    rows={4}
                  />
                </div>
                <div className="modal-buttons">
                  <button type="button" className="cancel-button" onClick={() => setSaveModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="save-button">
                    Save Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Monetize Modal */}
      <MonetizeModal
        isOpen={isMonetizeModalOpen}
        onClose={() => setMonetizeModalOpen(false)}
        projectTitle={projectDetails.title || 'Untitled Project'}
        codeSegments={codeSegments}
      />
    </div>
  );
}

export default CreateApp;



